import {CalendarEventResponse, CalendarEventCreate, CalendarEventUpdate, SyncStatus} from "../api-client";
import {WashEvent} from "../types";
import dayjs from "dayjs";
import logger from "../utils/logger";


export function mapCalendarEventToWashEvent(calendarEvent: CalendarEventResponse): WashEvent {
    const doctorId = calendarEvent.doctor ?? '';
    const roomId = calendarEvent.clinicId ?? '';
    const patientIdOrName = calendarEvent.patient ?? calendarEvent.unregisteredPatient ?? '';
    return {
        createdAt: calendarEvent.createdAt ?? '',
        id: calendarEvent.id ?? '',
        syncStatus: calendarEvent.syncStatus ?? '',
        title: calendarEvent.title ?? '',
        googleId: calendarEvent.googleId ?? '',
        start: isoStringToDate(calendarEvent.start),
        end: isoStringToDate(calendarEvent.end),
        user: calendarEvent.userId ?? 0,
        patient: patientIdOrName,
        doctor: doctorId,
        clinicId: roomId,
        notes: calendarEvent.notes ?? '',
        color: ''
    };
}

export function mapWashEventToCalendarEventCreate(washEvent: WashEvent, organizationId: string): CalendarEventCreate {
    // Assuming the IDs are UUIDs and using a simple regex pattern to check
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // Check if the patient field is an ID or a name
    const isExistingPatient = isUuid.test(washEvent.patient);

    let patientId = '';
    let unregisteredPatient = '';

    if (isExistingPatient) {
        // It's an ID, so we set it to patientId
        patientId = washEvent.patient;
    } else {
        // It's a name, so we set it to unregisteredPatient
        unregisteredPatient = washEvent.patient;
    }
    const syncStatus = Object.values(SyncStatus).includes(washEvent.syncStatus as SyncStatus)
        ? washEvent.syncStatus as SyncStatus
        : SyncStatus.CreatePending;

    return {
        title: washEvent.title,
        start: dateToLocalISO(washEvent.start),
        end: dateToLocalISO(washEvent.end),
        patient: patientId || undefined,
        unregisteredPatient: unregisteredPatient || undefined,
        doctor: washEvent.doctor || undefined,
        clinicId: washEvent.clinicId || undefined,
        notes: washEvent.notes || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        organizationId: organizationId,
        userId: washEvent.user || undefined,
        syncStatus: syncStatus,
    };
}

export function mapWashEventToCalendarEvent(washEvent: WashEvent): CalendarEventUpdate {
    // Assuming the IDs are UUIDs and using a simple regex pattern to check
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    // Check if the patient field is an ID or a name
    const isExistingPatient = isUuid.test(washEvent.patient);

    let patientId = '';
    let unregisteredPatient = '';

    if (isExistingPatient) {
        // It's an ID, so we set it to patientId
        patientId = washEvent.patient;
    } else {
        // It's a name, so we set it to unregisteredPatient
        unregisteredPatient = washEvent.patient;
    }
    const syncStatus = Object.values(SyncStatus).includes(washEvent.syncStatus as SyncStatus)
        ? washEvent.syncStatus as SyncStatus
        : SyncStatus.CreatePending;

    return {
        syncStatus: syncStatus,
        title: washEvent.title,
        googleId: washEvent.googleId,
        start: dateToLocalISO(washEvent.start),
        end: dateToLocalISO(washEvent.end),
        patient: patientId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        unregisteredPatient: unregisteredPatient,
        doctor: washEvent.doctor,
        notes: washEvent.notes,
        clinicId: washEvent.clinicId,
    };
}

export function mapCalendarEventsToWashEvents(calendarEvents: CalendarEventResponse[]): WashEvent[] {
    return calendarEvents.map((calendarEvent) => mapCalendarEventToWashEvent(calendarEvent));
}
export function dateToLocalISO(date: { getTimezoneOffset: () => any; getTime: () => number; }) {
    const off    = date.getTimezoneOffset()
    return (new Date(date.getTime() - off*60*1000).toISOString())
}

export function isoStringToDate(isoString: string | undefined): Date {
    if (!isoString) {
        return new Date();
    }

    const parts = isoString.split(/[-T:.Z]/).map(Number);
    if (parts.length < 6) {
        return new Date(); // Invalid ISO string format
    }

    return new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
}

export const getDoctorColor = (doctorId: string): string => {
    const index = Math.abs(stringHashCode(doctorId) % doctorColors.length);
    return doctorColors[index];
};

const doctorColors = [
    '#8b24a566', '#2CE3E366', '#3862ee66', '#3b486366', '#52c41a66',
    '#1890ff66', '#ff4d4f66', '#faad1466', '#9a9a9a66'
];

function stringHashCode(s: string): number {
    let hash = 0;
    if (s.length === 0) {
        return hash;
    }
    for (let i = 0; i < s.length; i++) {
        const character = s.charCodeAt(i);
        hash = ((hash << 5) - hash) + character;
        hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
}

export const deleteCalendarEvent = async (eventId: string, calendarApi: any) => {
    try {
        await calendarApi.deleteCalendarEvent(eventId);
    } catch (error) {
        logger.error("Error deleting calendar event", error);
    }
};

export const authenticationRetry = async (
    retries: number,
    delay: number,
    idx: number,
    checkAuthentication: () => Promise<any>
): Promise<boolean> => {
    if (idx >= retries - 1) {
        return true;
    }

    const retry = async () => {
        await new Promise(res => setTimeout(res, delay));
        await authenticationRetry(retries, delay, idx + 1, checkAuthentication);
    };

    try {
        const response = await checkAuthentication(); // Use the passed function
        if (response.data.status !== "Re-authentication required") {
            return false;
        }
        await retry();
    } catch (err) {
        await retry();
    }

    return true;
};